import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, Alert, Tabs, Tab } from '@mui/material';
import { PlayArrow, Refresh } from '@mui/icons-material';
import Editor from '@monaco-editor/react';

function HtmlCssWorkspace({ question, onCodeChange, initialCode = '' }) {
  const defaultHtml = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>HTML & CSS Challenge</title>
</head>
<body>
  <div class="container">
    <h1>Welcome to HTML & CSS</h1>
    <p>Edit the HTML and CSS to complete your challenge.</p>
    <button>Click Me</button>
  </div>
</body>
</html>`;

  const defaultCss = `* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: Arial, sans-serif;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.container {
  background: white;
  padding: 40px;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  text-align: center;
}

h1 {
  color: #333;
  margin-bottom: 20px;
}

p {
  color: #666;
  margin-bottom: 20px;
}

button {
  background: #667eea;
  color: white;
  border: none;
  padding: 12px 24px;
  font-size: 16px;
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.3s;
}

button:hover {
  background: #764ba2;
}`;

  const [html, setHtml] = useState(initialCode?.html || defaultHtml);
  const [css, setCss] = useState(initialCode?.css || defaultCss);
  const [activeTab, setActiveTab] = useState(0);
  const [previewKey, setPreviewKey] = useState(0);

  useEffect(() => {
    if (onCodeChange) {
      onCodeChange({ html, css });
    }
  }, [html, css, onCodeChange]);

  const generatePreview = () => {
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>${css}</style>
</head>
${html.includes('<body') ? html : `<body>${html}</body>`}
</html>`;
  };

  const refresh = () => {
    setPreviewKey(prev => prev + 1);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {question && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {question.name || 'HTML & CSS Challenge'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {question.task || question.question || question.text}
          </Typography>
        </Paper>
      )}

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="HTML" />
        <Tab label="CSS" />
        <Tab label="Preview" />
      </Tabs>

      {activeTab === 0 && (
        <Box sx={{ flexGrow: 1, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
          <Editor
            height="100%"
            defaultLanguage="html"
            value={html}
            onChange={(value) => setHtml(value || '')}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              automaticLayout: true,
              tabSize: 2,
            }}
          />
        </Box>
      )}

      {activeTab === 1 && (
        <Box sx={{ flexGrow: 1, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
          <Editor
            height="100%"
            defaultLanguage="css"
            value={css}
            onChange={(value) => setCss(value || '')}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              automaticLayout: true,
              tabSize: 2,
            }}
          />
        </Box>
      )}

      {activeTab === 2 && (
        <Box sx={{ flexGrow: 1, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', bgcolor: 'white' }}>
          <iframe
            key={previewKey}
            title="HTML/CSS Preview"
            srcDoc={generatePreview()}
            style={{ width: '100%', height: '100%', border: 'none' }}
            sandbox="allow-scripts"
          />
        </Box>
      )}

      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<PlayArrow />}
          onClick={() => setActiveTab(2)}
        >
          View Preview
        </Button>
        {activeTab === 2 && (
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={refresh}
          >
            Refresh Preview
          </Button>
        )}
      </Box>

      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>HTML & CSS Workspace</strong> - Create static web pages with HTML structure and CSS styling.
        Toggle between HTML, CSS, and live preview tabs.
      </Alert>
    </Box>
  );
}

export default HtmlCssWorkspace;
